import { useMutation } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { ServerCrash, Trash2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'

import { Toast } from '@/common/components/Toast'
import { useModalProvider } from '@/common/hooks/useModalProvider'
import { api, ApiError } from '@/lib/axios'
import { queryClient } from '@/lib/tanstack'

async function removeGenericContent(id: string) {
  await api.backend.delete(`/generic-content/${id}`)
}

export function useRemoveGenericContent() {
  const { t } = useTranslation('settings')
  const { handleModal, toggleLoading } = useModalProvider()

  const { mutateAsync } = useMutation<unknown, AxiosError<ApiError>, string>({
    mutationKey: ['/generic-content/id'],
    mutationFn: removeGenericContent,
    onSuccess: () => {
      toast(Toast, {
        data: {
          type: 'success',
          icon: Trash2,
          title: t(
            'generic_content.remove_generic_content.toast_success.title',
          ),
        },
      })
      queryClient.invalidateQueries({
        queryKey: ['generic-content/paginate'],
      })
    },
    onError: (error) => {
      toast(Toast, {
        data: {
          type: 'danger',
          icon: ServerCrash,
          title: t('generic_content.remove_generic_content.toast_error.title'),
          description:
            error?.response?.data?.friendlyMessage ||
            t('generic_content.remove_generic_content.toast_error.description'),
        },
      })
    },
  })

  const handleRemoveGenericContent = (id: string) => {
    handleModal({
      type: 'danger',
      title: t('generic_content.remove_generic_content.modal.title'),
      description: t(
        'generic_content.remove_generic_content.modal.description',
      ),
      confirmText: t(
        'generic_content.remove_generic_content.modal.button_confirm',
      ),
      onConfirm: async () => {
        toggleLoading()
        await mutateAsync(id).finally(() => {
          toggleLoading()
        })
      },
    })
  }

  return {
    handleRemoveGenericContent,
  }
}
